import { VdTracking } from '@vend/vend.js'
import type { VdToastNotificationService } from '../../../vend-ui/types'
import { CashMovementsManager } from '../../../managers/cash-movements-manager'

import { RegisterClosureManager } from '../../../managers/register-closure-manager'
import { RegisterManager } from '../../../managers/register-manager'

/**
 * Controller class for setting cash float modal
 *
 * @class SetCashFloatController
 */
export class SetCashFloatController {
  public awaitingServerResponse: $TSFixMe
  public cashMovementRequestFailed: $TSFixMe
  constructor(
    public close: $TSFixMe,
    public options: $TSFixMe,
    private vdToastNotificationService: VdToastNotificationService,
    private registerManager: RegisterManager,
    private vdTracking: VdTracking,
    private registerClosureManager: RegisterClosureManager,
    private cashMovementsManager: CashMovementsManager
  ) {
    'ngInject'
  }

  /**
   * Responsible for setting the opening cash float. Employed when openRegisterAndSetFloat has been partially
   * successful in that the register opened but setting the opening cash float failed. In that instance we just need
   * to retry setting the opening cash float.
   *
   * @method recordCashMovement
   *
   * @param  {Object} cashFloatSettings
   *         The cash movement object for the opening cash float.
   */
  public recordCashMovement(cashFloatSettings: any) {
    this.cashMovementsManager
      .recordCashMovement(cashFloatSettings)
      .then((cashMovement: $TSFixMe) => this.close(cashMovement))
      .catch(() => this._handleCashMovementFailure())
  }

  /**
   * Handles the cancelling or skipping of setting an initial float for a register. If we have attempted to set a cash
   * float but have failed, we allow the retailer to skip setting a float.
   *
   * @method cancelOpenRegisterAndSetFloat
   */
  public cancelOpenRegisterAndSetFloat() {
    if (this.cashMovementRequestFailed) {
      this.vdToastNotificationService.negative({
        message: `We had a problem saving the opening cash drawer amount. To retry, open Cash Management in the side
          menu bar.`,
      })
    }

    this.close()
  }

  /**
   * Opens the register (if not already open) and records the initial cash float for the register.
   *
   * @method openRegisterAndSetFloat
   *
   * @param  {Object} cashFloatSettings
   *         The cash movement object for the opening cash float.
   */
  public openRegisterAndSetFloat(cashFloatSettings: any) {
    const register = this.registerManager.getActiveRegister()
    this.awaitingServerResponse = true

    if (
      !this.cashMovementRequestFailed &&
      !this.registerManager.isActiveRegisterOpen()
    ) {
      this.registerClosureManager
        .openRegisterAndSetFloat(register, cashFloatSettings)
        .then(({ cashMovement }: $TSFixMe) => this.close(cashMovement))
        .catch(() => this._handleCashMovementFailure())
    } else {
      this.recordCashMovement(cashFloatSettings)
    }
  }

  /**
   * Handler for dealing with failed cash movements.
   *
   * @private
   * @method _handleCashMovementFailure
   */
  private _handleCashMovementFailure() {
    this.awaitingServerResponse = false

    // Close the modal if the register failed to open
    if (!this.registerManager.isActiveRegisterOpen()) {
      this.close()
    } else {
      // Register is open but setting the opening cash float failed
      this.cashMovementRequestFailed = true
      this.vdToastNotificationService.negative({
        message: `We had a problem saving the opening cash drawer amount. Retry, or skip this for now.`,
      })
      this.vdTracking.trackUserEvent({
        eventName: 'Cash Management',
        metadata: {
          'Failed Cash Float': true,
        },
      })
    }
  }
}
